/**
 * Tailored teaching puzzles: a puzzle that practices the move one section
 * week's AI feedback suggested, for the section leaders who taught it.
 */
import {z} from 'zod';

import {SimTeachPuzzleSchema, type SimTeachPuzzle} from './simTeachPuzzle';

/** The Cloud Function that drafts a tailored puzzle for an admin. */
export const DRAFT_TAILORED_PUZZLE_FUNCTION = 'drafttailoredpuzzle';

/** Request for {@link DRAFT_TAILORED_PUZZLE_FUNCTION}. */
export interface DraftTailoredPuzzleRequest {
  /** The course. */
  courseId: string;
  /** The section. */
  sectionId: string;
  /** The section week whose feedback moment to use. */
  sectionWeek: number;
  /** A steer for the writer, e.g. which misconception to use. Omitted: none. */
  guidance?: string;
}

/** Response from {@link DRAFT_TAILORED_PUZZLE_FUNCTION}. */
export const DraftTailoredPuzzleResponseSchema = z.object({
  /** `ready` with a puzzle, `skipped` when the moment can't make one, `failed` when the draft broke the code checks. */
  status: z.enum(['ready', 'skipped', 'failed']),
  /** The puzzle, when ready. */
  puzzle: SimTeachPuzzleSchema.optional(),
  /** Why it was skipped or failed. */
  reason: z.string().optional(),
});

/** Response from {@link DRAFT_TAILORED_PUZZLE_FUNCTION}. */
export type DraftTailoredPuzzleResponse = z.infer<
  typeof DraftTailoredPuzzleResponseSchema
>;

/** Firestore subcollection (under `course/{courseId}`) of generation jobs. */
export const TAILORED_PUZZLE_JOBS_COLLECTION = 'tailoredPuzzleJobs';

/** Prefix of the skip reason for a moment about participation, not content. */
export const PARTICIPATION_MOMENT_SKIP_PREFIX = 'Participation moment';

/** A generation job's statuses, in the order a run moves through them. */
const TAILORED_PUZZLE_JOB_STATUSES = [
  'queued',
  'generating',
  'ready',
  'skipped',
  'failed',
] as const;

/**
 * Where generating a section week's tailored puzzle stands: `queued` until a
 * worker takes it, `generating` while it's being written, `ready` once the
 * puzzle is saved, `skipped` when the moment can't make a puzzle, and
 * `failed` on an error. Jobs from before the reviewer was removed may say
 * `held`; they read as `failed`, so they can be retried.
 */
export const TailoredPuzzleJobStatusSchema = z.preprocess(
  value => (value === 'held' ? 'failed' : value),
  z.enum(TAILORED_PUZZLE_JOB_STATUSES),
);

/** A generation job's status. */
export type TailoredPuzzleJobStatus = z.infer<
  typeof TailoredPuzzleJobStatusSchema
>;

/**
 * One section week's generation job, keyed by the puzzle id. Server-written;
 * readable by admins and the section leaders in `forUids`.
 */
export const TailoredPuzzleJobSchema = z.object({
  /** Where it stands. */
  status: TailoredPuzzleJobStatusSchema,
  /** The section. */
  sectionId: z.string(),
  /** The section week. */
  sectionWeek: z.number().int().positive(),
  /** The section leaders the puzzle is for. */
  forUids: z.array(z.string()),
  /** When the latest run started (or was queued), in epoch ms. */
  startedAtMs: z.number(),
  /** When it ended, in epoch ms. Omitted: still running. */
  finishedAtMs: z.number().optional(),
  /** Why it was skipped or failed, for admins. Omitted: none. */
  reason: z.string().optional(),
  /** The model that wrote it. Omitted: none ran. */
  model: z.string().optional(),
});

/** One section week's generation job. */
export type TailoredPuzzleJob = z.infer<typeof TailoredPuzzleJobSchema>;

/**
 * Path of a tailored puzzle's generation job.
 *
 * @param args.courseId - The course.
 * @param args.puzzleId - The tailored puzzle's id.
 * @return `course/{courseId}/tailoredPuzzleJobs/{puzzleId}`.
 * @throws Never.
 */
export function tailoredPuzzleJobDocPath({
  courseId,
  puzzleId,
}: {
  courseId: string;
  puzzleId: string;
}): string {
  return `course/${courseId}/${TAILORED_PUZZLE_JOBS_COLLECTION}/${puzzleId}`;
}

/** A run still `generating` after this long died without recording it. */
export const STALE_TAILORED_PUZZLE_JOB_MS = 15 * 60_000;

/**
 * Whether a job claims to be running but has outlived any real run.
 *
 * @param args.job - The job.
 * @param args.nowMs - The current time, in epoch ms.
 * @return True for a `generating` job older than
 *   {@link STALE_TAILORED_PUZZLE_JOB_MS}.
 * @throws Never.
 */
export function isTailoredPuzzleJobStale({
  job,
  nowMs,
}: {
  job: Pick<TailoredPuzzleJob, 'status' | 'startedAtMs'>;
  nowMs: number;
}): boolean {
  return (
    job.status === 'generating' &&
    nowMs - job.startedAtMs >= STALE_TAILORED_PUZZLE_JOB_MS
  );
}

/** The course feature that turns on generating tailored puzzles. */
export const TAILORED_PUZZLES_FEATURE = 'tailored-puzzles';

/** Prefix of every tailored puzzle's id. */
export const TAILORED_PUZZLE_ID_PREFIX = 'tailored-';

/**
 * The id of a section week's tailored puzzle, lowercased because puzzle ids
 * are.
 *
 * @param args.sectionId - The section.
 * @param args.sectionWeek - The section week whose feedback it comes from.
 * @return `tailored-<sectionid>-w<week>`.
 * @throws Never.
 */
export function tailoredPuzzleId({
  sectionId,
  sectionWeek,
}: {
  sectionId: string;
  sectionWeek: number;
}): string {
  return `${TAILORED_PUZZLE_ID_PREFIX}${sectionId.toLowerCase()}-w${String(sectionWeek)}`;
}

/**
 * Whether a viewer may see and play a puzzle. A published puzzle is for
 * everyone who can play puzzles; an unpublished one only for admins and, if
 * it's tailored, the section leaders it was made for, or any section leader
 * when `isSectionLeader` is set (prep playlists hand out other sections'
 * tailored puzzles, which SLs can already read).
 *
 * @param args.puzzle - The puzzle.
 * @param args.uid - The viewer. Omitted: signed out, so only published ones.
 * @param args.isAdmin - Whether the viewer is a course admin.
 * @param args.isSectionLeader - Whether the viewer is a section leader in
 *   the course. Omitted: false, so only their own tailored puzzles.
 * @return True when the viewer may see it.
 * @throws Never.
 */
export function isSimTeachPuzzleVisible({
  puzzle,
  uid,
  isAdmin,
  isSectionLeader = false,
}: {
  puzzle: SimTeachPuzzle;
  uid?: string;
  isAdmin: boolean;
  isSectionLeader?: boolean;
}): boolean {
  if (isAdmin || puzzle.published === true) {
    return true;
  }
  if (uid === undefined || puzzle.tailored === undefined) {
    return false;
  }
  return isSectionLeader || isTailoredFor({puzzle, uid});
}

/** How a tailored briefing names the SL's own feedback. */
const OWN_FEEDBACK_PHRASE = 'Your feedback suggested';

/**
 * A tailored puzzle's briefing as a viewer reads it: for someone it wasn't
 * made for (a borrowed puzzle), "Your feedback suggested" reads "Another
 * SL's feedback suggested".
 *
 * @param args.puzzle - The puzzle.
 * @param args.uid - The viewer. Omitted: signed out, read as not its SL.
 * @return The briefing, or undefined when it has none.
 * @throws Never.
 */
export function briefingForViewer({
  puzzle,
  uid,
}: {
  puzzle: SimTeachPuzzle;
  uid?: string;
}): string | undefined {
  const {briefing} = puzzle;
  if (
    briefing === undefined ||
    puzzle.tailored === undefined ||
    (uid !== undefined && isTailoredFor({puzzle, uid}))
  ) {
    return briefing;
  }
  return briefing.replace(
    OWN_FEEDBACK_PHRASE,
    "Another SL's feedback suggested",
  );
}

/**
 * Whether a puzzle was tailored for this section leader.
 *
 * @param args.puzzle - The puzzle.
 * @param args.uid - The section leader.
 * @return True when the puzzle names them in `tailored.forUids`.
 * @throws Never.
 */
export function isTailoredFor({
  puzzle,
  uid,
}: {
  puzzle: SimTeachPuzzle;
  uid: string;
}): boolean {
  return (puzzle.tailored?.forUids ?? []).includes(uid);
}
